export type CampaignProcessingStatus = 'on-time' | 'processing' | 'delayed' | 'overdue';

export interface CampaignTimeStatus {
  isOverdue: boolean;
  processingStatus: CampaignProcessingStatus;
  minutesLate: number;
  hoursLate: number;
  daysLate: number;
  formattedLateness: string;
  statusLabel: string;
}

const PROCESSING_THRESHOLD_MINUTES = 20;
const DELAYED_THRESHOLD_MINUTES = 60;

export function getCampaignTimeStatus(
  scheduledAt: string | null,
  status: string,
  sentAt: string | null = null
): CampaignTimeStatus {
  const defaultStatus: CampaignTimeStatus = {
    isOverdue: false,
    processingStatus: 'on-time',
    minutesLate: 0,
    hoursLate: 0,
    daysLate: 0,
    formattedLateness: '',
    statusLabel: '',
  };

  if (!scheduledAt || status !== 'scheduled') {
    return defaultStatus;
  }

  const now = new Date();
  const scheduled = new Date(scheduledAt);

  if (scheduled > now) {
    return defaultStatus;
  }

  const diffMs = now.getTime() - scheduled.getTime();
  const minutesLate = Math.floor(diffMs / (1000 * 60));
  const hoursLate = Math.floor(diffMs / (1000 * 60 * 60));
  const daysLate = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutesLate < PROCESSING_THRESHOLD_MINUTES) {
    return {
      isOverdue: false,
      processingStatus: 'processing',
      minutesLate,
      hoursLate: 0,
      daysLate: 0,
      formattedLateness: '',
      statusLabel: 'Wordt verwerkt',
    };
  }

  if (minutesLate < DELAYED_THRESHOLD_MINUTES) {
    return {
      isOverdue: false,
      processingStatus: 'delayed',
      minutesLate,
      hoursLate: 0,
      daysLate: 0,
      formattedLateness: `${minutesLate} min vertraagd`,
      statusLabel: 'Vertraagd',
    };
  }

  let formattedLateness = '';
  if (daysLate > 0) {
    formattedLateness = `${daysLate} dag${daysLate > 1 ? 'en' : ''} te laat`;
  } else if (hoursLate > 0) {
    formattedLateness = `${hoursLate} uur te laat`;
  } else {
    formattedLateness = `${minutesLate} minuten te laat`;
  }

  return {
    isOverdue: true,
    processingStatus: 'overdue',
    minutesLate,
    hoursLate,
    daysLate,
    formattedLateness,
    statusLabel: 'Te Laat',
  };
}

export function isDateInPast(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  return date < now;
}

export function formatScheduledDate(dateStr: string, isOverdue: boolean = false): string {
  const date = new Date(dateStr);
  const formatted = date.toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return formatted;
}
